import { useEffect, useMemo, useRef } from 'react';
import { Link, isRouteErrorResponse, useLocation, useRouteError } from 'react-router-dom';
import { House, LayoutDashboard, Loader2, RotateCw, TriangleAlert } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  canAutoReloadForChunk,
  claimAutoReloadForChunk,
  describeError,
  isChunkLoadError,
  reloadPage,
  shouldShowErrorDetails,
} from '@/lib/routeError';
import { reportClientError } from '@/lib/clientErrorReporter';
import { NotFound } from './NotFound';

interface ErrorPageProps {
  /**
   * `content` sits in a layout's content area (the portal's sidebar and top
   * bar keep working); `page` fills the screen, for when the layout itself
   * is what crashed.
   */
  variant?: 'page' | 'content';
  /** Where the second button leads: the portal dashboard or the public home page. */
  home?: 'portal' | 'public';
}

/**
 * errorElement for render crashes and route errors.
 *
 * A failed chunk load (a tab still on the previous deploy) reloads the page
 * once by itself; anything else, or a chunk that fails again within a minute,
 * shows "Something went wrong" and is reported to the backend once.
 */
export const ErrorPage = ({ variant = 'page', home = 'public' }: ErrorPageProps) => {
  const error = useRouteError();
  const { pathname } = useLocation();
  const handledRef = useRef<{ error: unknown } | null>(null);

  // A route 404 keeps the normal not-found page; it is not a crash.
  const notFound = isRouteErrorResponse(error) && error.status === 404;
  // `reloading` is a read-only check here, memoised per error so the view does
  // not flip once the effect below stamps the reload time.
  const { chunkError, reloading } = useMemo(() => {
    const chunk = !notFound && isChunkLoadError(error);
    return { chunkError: chunk, reloading: chunk && canAutoReloadForChunk() };
  }, [error, notFound]);

  useEffect(() => {
    // Once per error: StrictMode runs effects twice in development.
    if (notFound || handledRef.current?.error === error) return;
    handledRef.current = { error };

    // Stamping the time is what stops a chunk that fails again within a minute
    // from reloading in a loop, so no stamp, no automatic reload.
    if (reloading && claimAutoReloadForChunk()) {
      reloadPage();
      return;
    }
    reportClientError(error, { kind: chunkError ? 'chunk' : 'render', path: pathname });
  }, [error, notFound, chunkError, reloading, pathname]);

  if (notFound) return <NotFound />;

  const containerClass = cn(
    'w-full flex items-center justify-center p-4',
    variant === 'page' ? 'min-h-screen bg-[#F8F9FF]' : 'py-16',
  );

  if (reloading) {
    return (
      <div className={containerClass} role="status">
        <div className="flex flex-col items-center gap-4 text-gray-600">
          <div className="flex items-center gap-3">
            <Loader2 className="h-5 w-5 animate-spin text-[#3C3295]" />
            <span>Loading the latest version&hellip;</span>
          </div>
          <Button variant="outline" size="sm" onClick={reloadPage}>
            <RotateCw size={14} />
            Reload now
          </Button>
        </div>
      </div>
    );
  }

  const { name, message, stack } = describeError(error);

  return (
    <div className={containerClass}>
      <div className="max-w-lg w-full text-center">
        <div className="mb-6 flex justify-center">
          <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center">
            <TriangleAlert className="w-10 h-10 text-amber-600" />
          </div>
        </div>

        <h1 className="text-2xl font-bold text-gray-900 mb-3">Something went wrong</h1>
        <p className="text-gray-600 mb-8">
          {chunkError
            ? 'This page could not be loaded. A new version of the portal may have been released; reloading usually fixes it.'
            : 'An unexpected error stopped this page from loading. Reload to try again, and if it keeps happening, contact your administrator.'}
        </p>

        <div className="flex flex-col-reverse sm:flex-row gap-3 justify-center">
          <Button asChild variant="outline" className="w-full sm:w-auto">
            {home === 'portal' ? (
              <Link to="/portal">
                <LayoutDashboard size={16} />
                Go to dashboard
              </Link>
            ) : (
              <Link to="/">
                <House size={16} />
                Go to home
              </Link>
            )}
          </Button>
          <Button
            onClick={reloadPage}
            className="w-full sm:w-auto bg-linear-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white transition-colors"
          >
            <RotateCw size={16} />
            Reload
          </Button>
        </div>

        {shouldShowErrorDetails() && (
          <details className="mt-8 text-left rounded-lg border border-gray-200 bg-white">
            <summary className="cursor-pointer select-none px-4 py-3 text-sm font-medium text-gray-700">
              Technical details
            </summary>
            <div className="border-t border-gray-200 px-4 py-3">
              <p className="font-mono text-sm text-gray-900 break-words">
                {name}: {message}
              </p>
              {stack && (
                <pre className="mt-3 max-h-64 overflow-auto whitespace-pre-wrap break-all text-xs text-gray-500">
                  {stack}
                </pre>
              )}
            </div>
          </details>
        )}
      </div>
    </div>
  );
};
